import React, { useState, useEffect, useCallback } from "react";
import {
  getAllUsersAdmin,
  updateUserDetails,
  deleteUser,
} from "../../apis/UserApis";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { SkeletonAdminTable } from "../Skeletons";

export default function AdminUsersTab() {
  const { user } = useAuth();
  const { successToast, errorToast } = useToastHelpers();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const ITEMS_PER_PAGE = 15;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllUsersAdmin(user.token);
      setUsers(Array.isArray(data) ? data : data?.users || []);
    } catch (error) {
      errorToast("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [user.token, errorToast]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Filter users
  const filteredUsers = users.filter((u) => {
    const fullName = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
    const matchesSearch =
      !search ||
      fullName.includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());

    const matchesRole = !roleFilter || u.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Stats
  const adminCount = users.filter((u) => u.role === "admin").length;
  const subscribedCount = users.filter((u) => u.isSubscribe).length;

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRoleId(userId);
    try {
      await updateUserDetails(userId, { role: newRole }, user.token);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u))
      );
      successToast(`User role updated to ${newRole}`);
    } catch (error) {
      errorToast("Failed to update user role");
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleDelete = async (userId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone."
      )
    )
      return;

    setDeletingUserId(userId);
    try {
      await deleteUser(userId, user.token);
      successToast("User deleted successfully");
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (error) {
      errorToast("Failed to delete user");
    } finally {
      setDeletingUserId(null);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setRoleFilter("");
    setCurrentPage(1);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="ca-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <i className="ri-user-line text-2xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {users.length}
              </p>
            </div>
          </div>
        </div>
        <div className="ca-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <i className="ri-shield-user-line text-2xl text-purple-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Admins</p>
              <p className="text-2xl font-semibold text-gray-900">
                {adminCount}
              </p>
            </div>
          </div>
        </div>
        <div className="ca-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <i className="ri-notification-line text-2xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Subscribed</p>
              <p className="text-2xl font-semibold text-gray-900">
                {subscribedCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters/Search */}
      <div className="ca-card p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Users
            </label>
            <div className="relative">
              <input
                type="search"
                placeholder="Search by name or email..."
                className="ca-input pl-10 text-sm"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400"></i>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Role
            </label>
            <select
              className="ca-input text-sm"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className="ca-btn-secondary w-full px-4 py-2"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="ca-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Users ({filteredUsers.length})
          </h3>
        </div>

        {loading ? (
          <div className="p-6">
            <SkeletonAdminTable rows={10} columns={6} />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <i className="ri-user-line text-5xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">No users found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Last Active
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                            {u.firstName?.charAt(0) || "U"}
                            {u.lastName?.charAt(0) || ""}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {u.firstName} {u.lastName}
                            </div>
                            {u.streakCount > 0 && (
                              <div className="text-xs text-orange-600">
                                🔥 {u.streakCount} day streak
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {u.email}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={u.role}
                          onChange={(e) =>
                            handleRoleChange(u.id, e.target.value)
                          }
                          disabled={updatingRoleId === u.id}
                          className={`text-xs font-semibold rounded-full px-3 py-1 border-0 cursor-pointer ${
                            u.role === "admin"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-gray-100 text-gray-800"
                          } ${updatingRoleId === u.id ? "opacity-50" : ""}`}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        {u.isSubscribe ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            <i className="ri-notification-line"></i>
                            Subscribed
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                            Not Subscribed
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(u.lastActiveAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <div className="flex items-center justify-center space-x-1">
                          <button
                            onClick={() => setSelectedUser(u)}
                            className="inline-flex items-center px-2 py-1.5 rounded-lg text-gray-700 hover:bg-gray-100"
                            title="View Details"
                          >
                            <i className="ri-eye-line text-blue-600"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(u.id)}
                            disabled={deletingUserId === u.id}
                            className="inline-flex items-center px-2 py-1.5 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                            title="Delete"
                          >
                            {deletingUserId === u.id ? (
                              <i className="ri-loader-4-line animate-spin text-red-600"></i>
                            ) : (
                              <i className="ri-delete-bin-line text-red-600"></i>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                  {Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)}{" "}
                  of {filteredUsers.length} users
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-lg border border-gray-300 text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white"
                            : "border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-lg border border-gray-300 text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}

function UserDetailsModal({ user, onClose }) {
  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">User Details</h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white text-2xl"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Avatar & Name */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3">
              {user.firstName?.charAt(0) || "U"}
              {user.lastName?.charAt(0) || ""}
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              {user.firstName} {user.lastName}
            </h3>
            <p className="text-gray-500">{user.email}</p>
            <span
              className={`inline-block mt-2 px-3 py-1 text-xs font-semibold rounded-full ${
                user.role === "admin"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {user.role?.toUpperCase()}
            </span>
          </div>

          {/* Details Grid */}
          <div className="space-y-4">
            <DetailRow label="User ID" value={user.id} />
            <DetailRow
              label="Subscribed"
              value={user.isSubscribe ? "Yes" : "No"}
            />
            <DetailRow label="Streak Count" value={user.streakCount || 0} />
            <DetailRow label="Age" value={user.age || "—"} />
            <DetailRow label="Address" value={user.address || "—"} />
            <DetailRow label="Last Active" value={formatDate(user.lastActiveAt)} />
            <DetailRow label="Updated At" value={formatDate(user.updatedAt)} />

            {/* Preferences */}
            {user.preferences && (
              <div className="pt-4 border-t border-gray-200">
                <h4 className="font-medium text-gray-700 mb-3">Preferences</h4>
                <div className="space-y-2 text-sm">
                  {user.preferences.jobType?.length > 0 && (
                    <div>
                      <span className="text-gray-500">Job Types:</span>{" "}
                      {user.preferences.jobType.join(", ")}
                    </div>
                  )}
                  {user.preferences.location?.length > 0 && (
                    <div>
                      <span className="text-gray-500">Locations:</span>{" "}
                      {user.preferences.location.join(", ")}
                    </div>
                  )}
                  {user.preferences.experienceLevel?.length > 0 && (
                    <div>
                      <span className="text-gray-500">Experience:</span>{" "}
                      {user.preferences.experienceLevel.join(", ")}
                    </div>
                  )}
                  {user.preferences.skills?.length > 0 && (
                    <div>
                      <span className="text-gray-500">Skills:</span>{" "}
                      {user.preferences.skills.join(", ")}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Badges & Certificates counts */}
            <div className="flex gap-4 pt-4 border-t border-gray-200">
              <div className="flex-1 text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {user.badges?.length || 0}
                </div>
                <div className="text-xs text-gray-500">Badges</div>
              </div>
              <div className="flex-1 text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {user.certificates?.length || 0}
                </div>
                <div className="text-xs text-gray-500">Certificates</div>
              </div>
              <div className="flex-1 text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {user.subscriptions?.length || 0}
                </div>
                <div className="text-xs text-gray-500">Subscriptions</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium text-right max-w-[60%] truncate">
        {value}
      </span>
    </div>
  );
}
